// src/pages/notifications/ScheduledListPage.tsx
// Scheduled notifications list with cancel action.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useNotifications, useI18n } from '@react/hooks';
import { Button, Card, CardContent, Alert, ErrorAlert,
} from '@react/components';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatNotificationStatus } from '../../features/notifications/logic';
import { ComposeNotificationPanel } from '../../features/notifications/ComposeNotificationPanel';
import type { ScheduledNotification } from '@core/types/notifications/ScheduledNotification';

export const ScheduledListPage = observer(() => {
  const store = useNotifications();
  const { t } = useI18n();
  const [showCompose, setShowCompose] = useState(false);
  const [sentBanner, setSentBanner] = useState(false);

  useEffect(() => {
    store.ListNotificationsScheduled();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancel = async (id: string) => {
    if (!confirm(t('notifications.cancelScheduledConfirm'))) return;
    try {
      await store.CancelNotificationScheduled(id);
      store.ListNotificationsScheduled();
    } catch {
      // handled by store
    }
  };

  const items = (store.items ?? []) as unknown as ScheduledNotification[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('notifications.scheduled')}</h2>
        {!showCompose && (
          <Button size="sm" onClick={() => setShowCompose(true)}>{t('notifications.compose.title')}</Button>
        )}
      </div>

      {sentBanner && <Alert variant="success" dismissible onDismiss={() => setSentBanner(false)}>{t('notifications.compose.sentSuccess')}</Alert>}
      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {showCompose && (
        <ComposeNotificationPanel
          onClose={() => setShowCompose(false)}
          onSent={() => {
            setShowCompose(false);
            setSentBanner(true);
            store.ListNotificationsScheduled();
          }}
        />
      )}

      <Card>
        <CardContent>
          <Table hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('notifications.subject')}</TableHead>
                <TableHead>{t('notifications.scheduledAt')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('common.createdAt')}</TableHead>
                <TableHead>{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && !store.isLoading && <TableEmpty colSpan={5} message={t('notifications.noScheduled')} />}
              {items.map((item) => {
                const status = formatNotificationStatus(item.status ?? '');
                return (
                  <TableRow key={item.entity?.id}>
                    <TableCell className="text-sm font-medium">{item.notification?.subject || t('common.notSet')}</TableCell>
                    <TableCell className="text-sm">{item.scheduled_at ?? '-'}</TableCell>
                    <TableCell><StatusBadge variant={status.variant} label={t(status.labelKey)} size="sm" /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{item.created_at ?? '-'}</TableCell>
                    <TableCell>
                      {item.status === 'NOTIFICATION_STATUS_PENDING' && (
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(item.entity?.id ?? '')}>{t('notifications.cancel')}</Button>
                      )}
                      {item.cancellation_reason && (
                        <span className="text-xs text-muted-foreground">{item.cancellation_reason}</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
});

export default ScheduledListPage;
